import { FileFormatMap } from './file-upload-props';
import { FIELD_ERROR_CLASS, useError } from '../../field-error';
import React, { useCallback, useEffect, useState } from 'react';
import { EMPTY_VALUES_BY_TYPE, FIELD_TYPES } from '../../constants';
import FieldWrapper from '../../ui/field-wrapper';
import FieldDescription from '../../ui/field-description';
import { FileUpload as TPAFileUpload } from 'wix-ui-tpa/cssVars';
import classNames from 'classnames';
import { FieldLabel } from '../../ui/field-label/field-label';
import { classes } from './file-upload.st.css';
import { useTranslation } from '../../contexts/translation-context';
import { useUploadFile } from './use-upload-file';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
function convertValuesToFiles(_value) {
  if (_value === void 0) {
    _value = [];
  }
  const value = _value ? _value : [];
  try {
    const files = value.map(v => {
      return {
        file: new File([new Int8Array(0)], v.displayName, {
          type: v.fileType
        }),
        state: 'uploaded'
      };
    });
    return files;
  } catch (e) {
    console.error(e);
  }
  return [];
}
export const FileUpload = _ref => {
  let {
    label,
    description,
    value,
    onChange,
    error,
    placeholder,
    customErrors,
    disabled = false,
    buttonText,
    uploadFileFormats,
    valueSliderLimit,
    required,
    hideLabel,
    hideAsterisk,
    explanationText,
    fieldType = FIELD_TYPES.FILE_UPLOAD
  } = _ref;
  const {
    hasError,
    errorMessage
  } = useError(error, customErrors);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(value || []);
  const [_error, setError] = useState();
  const uploadFiles = useUploadFile();
  const {
    t
  } = useTranslation();
  const forcedState = useForcedState();
  const hover = forcedState.includes(ForcedState.BUTTONS_HOVER);
  useEffect(() => {
    if (value || value === EMPTY_VALUES_BY_TYPE.FILE_UPLOAD) {
      const errorFiles = files.filter(f => f.state === 'error');
      updateFiles(convertValuesToFiles(value).concat(errorFiles));
    }
  }, [value]);
  useEffect(() => {
    onChange == null || onChange((value || []).concat(uploadedFiles));
  }, [uploadedFiles]);
  const validateFilesLimit = useCallback(filesCount => {
    setError(filesCount > valueSliderLimit ? t("form.file-upload.error.limit", {
      limit: valueSliderLimit
    }) : undefined);
  }, [valueSliderLimit, t]);
  const getExplanationText = useCallback(() => {
    if (!files.length) {
      return explanationText;
    }
    let _explanationText = explanationText || t("form.file-upload.explanation-text", {
      count: files.length
    });
    const uploadingCount = files.filter(f => f.state === 'uploading').length;
    if (uploadingCount) {
      const uploadedCount = files.filter(f => f.state === 'uploaded' && f.file.size > 0).length;
      const progress = uploadedCount / (uploadedCount + uploadingCount);
      _explanationText += ' ' + t("form.file-upload.uploading", {
        count: Math.round(progress * 100)
      });
    }
    return _explanationText;
  }, [files, explanationText]);
  const updateFiles = useCallback(uploadFiles => {
    setFiles(uploadFiles);
    validateFilesLimit(uploadFiles.length);
  }, [validateFilesLimit]);
  const _uploadFiles = useCallback(async filesToUpload => {
    const errorMessage = t('form.file-upload.error.upload-failed');
    const newUploadedFiles = await uploadFiles(filesToUpload, file => {
      setFiles(files => files.map(f => f.file === file ? {
        file,
        state: 'uploaded'
      } : f));
    }, (_err, file) => {
      setFiles(files => files.map(f => f.file === file ? {
        file,
        state: 'error',
        errorMessage
      } : f));
    });
    setUploadedFiles(newUploadedFiles);
  }, [uploadFiles, t]);
  const handleChange = async _files => {
    const filesArr = Array.from(_files);
    const end = valueSliderLimit - files.length;
    const filesToUpload = filesArr.slice(0, end);
    const filesToAdd = filesToUpload.map((_file, _index) => {
      return {
        file: _file,
        state: 'uploading'
      };
    });
    const excessFiles = filesArr.slice(end).map((_file, _index) => {
      return {
        file: _file,
        state: 'error'
      };
    });
    updateFiles(files.concat(filesToAdd, excessFiles));
    await _uploadFiles(filesToAdd);
  };
  const handleDelete = index => {
    const fileToDelete = files[index];
    if (value && onChange && fileToDelete.state !== 'error') {
      const filteredValues = value.filter(f => f.displayName !== fileToDelete.file.name);
      onChange(filteredValues);
    } else {
      const filteredFiles = files.filter(f => f !== fileToDelete);
      updateFiles(filteredFiles);
    }
  };
  const accept = uploadFileFormats.map(format => FileFormatMap[format]).join();
  return /*#__PURE__*/React.createElement(FieldWrapper, {
    fieldType: fieldType,
    dataHook: "file-upload"
  }, /*#__PURE__*/React.createElement(FieldDescription, {
    description: description
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement(TPAFileUpload, {
    error: !!_error || hasError,
    errorMessage: _error || errorMessage,
    newErrorMessage: true,
    buttonText: buttonText,
    accept: accept,
    className: classNames(classes.root, classes.crop, {
      [FIELD_ERROR_CLASS]: hasError,
      [classes.hover]: hover
    })
    // @ts-expect-error
    ,
    label: /*#__PURE__*/React.createElement(FieldLabel, {
      required: required,
      hideAsterisk: hideAsterisk,
      label: label,
      hideLabel: hideLabel
    }),
    explanationText: getExplanationText(),
    onDeleteClick: handleDelete,
    ariaLabel: label,
    multiple: valueSliderLimit > 1,
    files: files,
    onChange: handleChange,
    disabled: disabled,
    placeholder: placeholder
  }))));
};
export default FileUpload;
//# sourceMappingURL=file-upload.js.map